import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Card } from "./Card";
import { Skeleton } from "./Skeleton";

/**
 * Fades real content in once loading ends, so the swap is a transition rather than a jump cut.
 *
 * Keyed on `loading` so React remounts the subtree and the fade replays each time — without the
 * key the opacity class changes on an already-mounted node and the browser has nothing to
 * animate from.
 */
export function FadeIn({
  loading,
  skeleton,
  children,
}: {
  loading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}) {
  return (
    <div key={loading ? "loading" : "loaded"} className="animate-fade-in">
      {loading ? skeleton : children}
    </div>
  );
}

/** Two quota cards: a label/value row, a meter, and a caption — the real card's exact shape. */
export function QuotaCardsSkeleton() {
  return (
    <div className="grid gap-px bg-border sm:grid-cols-2">
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-3 bg-surface p-5">
          <div className="flex items-baseline justify-between gap-3">
            <Skeleton variant="text" className="h-4 w-24" />
            <Skeleton variant="text" className="h-4 w-16" />
          </div>
          <Skeleton className="h-2.5 w-full rounded-pill" />
          <Skeleton variant="text" className="h-3 w-3/4" />
        </div>
      ))}
    </div>
  );
}

/** Four stat cards: icon tile, uppercase label, large number. */
export function StatStripSkeleton() {
  return (
    <div className="grid gap-px border-t border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 bg-surface p-4">
          <Skeleton className="size-11 shrink-0 rounded-card" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton variant="text" className="h-3 w-20" />
            <Skeleton variant="text" className="h-7 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A webhook endpoint card: URL, status pill, event tags, and the actions row. */
export function WebhookCardsSkeleton({ count = 2 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }, (_, i) => (
        <Card key={i} padding="lg" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton variant="text" className="h-4 w-2/3" />
              <div className="flex gap-1.5">
                <Skeleton className="h-6 w-28 rounded-pill" />
                <Skeleton className="h-6 w-32 rounded-pill" />
              </div>
            </div>
            <Skeleton className="h-6 w-16 rounded-pill" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[20, 16, 14, 16].map((w, j) => (
              <Skeleton key={j} className="h-8 rounded-control" width={`${w * 4}px`} />
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Stacked label + input pairs, for a form whose values arrive from the server. */
export function FormSkeleton({
  fields = 3,
  className,
}: {
  fields?: number;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <Skeleton variant="text" className="h-3.5 w-24" />
          <Skeleton className="h-10 w-full rounded-control" />
        </div>
      ))}
    </div>
  );
}
